import React, { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useUpdateEffect } from "react-use";
import { Body2, Label, LayerContextProvider, layers } from "@activecollab/components";
import { List } from "immutable";
import isEqual from "lodash/isEqual";
import PropTypes from "prop-types";
import { v4 } from "uuid";
import { TaskEstimatesContainer } from "~/components/tasks/TaskEstimatesContainer";
import LabelMap from "~/records/record.label_map.jsx";
import {
  canSeeCallToUpgradeForTimeEstimates,
  hasCollaborativePermissionsOnProject,
} from "~/store/selectors/selector.users";
import { TaskRescheduleAction } from "./actions/TaskRescheduleAction.jsx";
import { SelectTaskLabels } from "./SelectTaskLabels.js";
import TaskProperties from "./TaskProperties/TaskProperties.jsx";
import FormatDateRange from "../Formatters/FormatDateRange.jsx";
import { Divider, FormCheckbox } from "../ModalObjects";
import SelectDates from "../Selectors/SelectDates.jsx";
import ProjectTaskListSelectContainer from "../task_list/ProjectTaskListSelectContainer.jsx";
import ProjectUserSelectContainer from "../Users/ProjectUserSelectContainer.jsx";

const TaskPanel = ({
  id,
  projectId,
  isStrictProject,
  assigneeId,
  startOn,
  dueOn,
  labels,
  labelIds: defaultLabelIds,
  estimate,
  jobTypeId,
  hiddenFromClients,
  important,
  canSeeTimeEstimates,
  loggedUserIsClient,
  canEdit,
  taskListId,
  fakeAssigneeName,
  canLoggedUserAddTaskToPriority,
  isSplit,
  onChange,
  hideFromClients,
  onTaskListChange,
}) => {
  const { t } = useTranslation();
  const canEditInStrictProject = useSelector((state) => hasCollaborativePermissionsOnProject(state, projectId));
  const [labelIds, setLabelIds] = useState(() => defaultLabelIds);

  const canSeeCallToUpgrade = useSelector(canSeeCallToUpgradeForTimeEstimates);

  const previous = useRef(defaultLabelIds);

  const handleChangeAssignee = useCallback(
    (id) => {
      if (typeof onChange === `function`) {
        onChange({ assignee_id: id });
      }
    },
    [onChange]
  );

  const handleChangeLabels = useCallback((labels) => {
    setLabelIds(labels.labelIds);
  }, []);

  const handleClose = useCallback(() => {
    if (typeof onChange === "function" && !isEqual(labelIds.toArray().sort(), previous.current.toArray().sort())) {
      onChange({ labels: labelIds });
      previous.current = labelIds;
    }
  }, [labelIds, onChange]);

  const handleChangeEstimate = useCallback(
    (estimate, jobTypeId) => {
      if (typeof onChange === `function`) {
        onChange({ estimate, job_type_id: jobTypeId });
      }
    },
    [onChange]
  );

  const handleChangeHiddenFromClients = useCallback(() => {
    if (typeof onChange === `function`) {
      onChange({ is_hidden_from_clients: !hiddenFromClients });
    }
  }, [hiddenFromClients, onChange]);

  const handleChangePriority = useCallback(() => {
    if (typeof onChange === `function`) {
      onChange({ is_important: !important });
    }
  }, [onChange, important]);

  const handleChangeTaskList = useCallback(
    (id) => {
      if (typeof onTaskListChange === `function`) {
        onTaskListChange({ task_list_id: id });
      }
    },
    [onTaskListChange]
  );

  const handleDateChange = useCallback(
    ({ from, to }) => {
      if (typeof onChange === `function`) {
        onChange({
          start_on: from,
          due_on: to,
        });
      }
    },
    [onChange]
  );

  const isMultiDate = useMemo(() => {
    return startOn && dueOn && startOn !== dueOn;
  }, [startOn, dueOn]);

  useUpdateEffect(() => {
    if (!isEqual(defaultLabelIds, labelIds)) {
      setLabelIds(defaultLabelIds);
    }
  }, [defaultLabelIds, labels]);

  const panelId = useMemo(() => `task_${v4()}`, []);

  const canManageHideFromClientsAndPriority = useMemo(
    () =>
      !isStrictProject
        ? (!loggedUserIsClient && hideFromClients) || canLoggedUserAddTaskToPriority
        : canEditInStrictProject,
    [canEditInStrictProject, canLoggedUserAddTaskToPriority, hideFromClients, loggedUserIsClient, isStrictProject]
  );

  return (
    <div tabIndex="0">
      <div className="task_properties">
        <div
          tabIndex="-1"
          className="tw-mb-6"
        >
          <Label
            size="small"
            className="tw-mb-1 tw-block"
          >
            {t("Task List")}
          </Label>
          <ProjectTaskListSelectContainer
            projectId={projectId}
            selectedId={taskListId}
            onChange={handleChangeTaskList}
            canEdit={canEdit}
          />
        </div>

        <div
          tabIndex="-1"
          className="tw-mb-6"
        >
          <Label
            size="small"
            className="tw-mb-1 tw-block"
          >
            {t("Assignee")}
          </Label>
          <ProjectUserSelectContainer
            projectId={projectId}
            selectedId={assigneeId}
            fakeAssigneeName={fakeAssigneeName}
            onChange={handleChangeAssignee}
            canEdit={canEditInStrictProject}
            hiddenFromClients={hiddenFromClients}
          />
        </div>

        <div
          tabIndex="-1"
          className="tw-mb-6"
        >
          <Label
            size="small"
            className="tw-mb-1 tw-block"
          >
            {isMultiDate ? t("Start and Due Date") : t("Due On")}
          </Label>
          <div>
            {!canEditInStrictProject ? (
              <Body2 color="secondary">
                {startOn > 0 && dueOn > 0 ? (
                  <FormatDateRange
                    start={startOn}
                    end={dueOn}
                  />
                ) : (
                  t("Not Set")
                )}
              </Body2>
            ) : (
              <LayerContextProvider value={{ zIndex: layers.skyscraper }}>
                <TaskRescheduleAction
                  id={id}
                  action="onChange"
                >
                  <SelectDates
                    userId={assigneeId}
                    selectedDays={{ from: startOn, to: dueOn }}
                    onChange={handleDateChange}
                    changeMode="atomic"
                  />
                </TaskRescheduleAction>
              </LayerContextProvider>
            )}
          </div>
        </div>

        <TaskProperties show={canSeeTimeEstimates || canSeeCallToUpgrade}>
          <TaskEstimatesContainer
            estimate={estimate}
            handleChange={handleChangeEstimate}
            jobTypeId={jobTypeId}
            canEdit={canEditInStrictProject}
            isSplit={isSplit}
            taskId={id}
          />
        </TaskProperties>

        <div
          tabIndex="-1"
          className="tw-mb-6"
        >
          <Label
            size="small"
            className="tw-mb-1 tw-block"
          >
            {t("Labels")}
          </Label>
          <SelectTaskLabels
            labels={labels}
            selected={labelIds}
            onChange={handleChangeLabels}
            projectId={projectId}
            onClose={handleClose}
            confirmDeselect
          />
        </div>

        {canManageHideFromClientsAndPriority ? (
          <div className="tw-mb-6">
            <TaskProperties show={!loggedUserIsClient && hideFromClients}>
              <FormCheckbox
                id={`${panelId}_panel_hidden_for_clients`}
                checked={hiddenFromClients}
                onChange={handleChangeHiddenFromClients}
                text={t("Hidden from clients")}
              />
            </TaskProperties>
            <TaskProperties show={canLoggedUserAddTaskToPriority}>
              <FormCheckbox
                id={`${panelId}_panel_high_priority`}
                checked={important}
                onChange={handleChangePriority}
                className="tw-mt-2"
                text={t("High priority")}
              />
            </TaskProperties>
          </div>
        ) : null}
      </div>
      <Divider className="tw-mt-0 tw-mb-6" />
    </div>
  );
};

TaskPanel.propTypes = {
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  projectId: PropTypes.number.isRequired,
  isStrictProject: PropTypes.bool,
  taskListId: PropTypes.number,
  assigneeId: PropTypes.number,
  startOn: PropTypes.number,
  dueOn: PropTypes.number,
  labels: PropTypes.instanceOf(LabelMap).isRequired,
  labelIds: PropTypes.instanceOf(List),
  estimate: PropTypes.string,
  jobTypeId: PropTypes.number,
  isSplit: PropTypes.bool,
  hiddenFromClients: PropTypes.bool,
  important: PropTypes.bool,
  canSeeTimeEstimates: PropTypes.bool,
  loggedUserIsClient: PropTypes.bool,
  canEdit: PropTypes.bool,
  onChange: PropTypes.func,
  onTaskListChange: PropTypes.func,
  fakeAssigneeName: PropTypes.string,
  canLoggedUserAddTaskToPriority: PropTypes.bool,
  hideFromClients: PropTypes.bool,
};

TaskPanel.defaultProps = {
  labelIds: List(),
  hiddenFromClients: false,
  important: false,
  canSeeTimeEstimates: false,
  loggedUserIsClient: false,
  canEdit: false,
  canLoggedUserAddTaskToPriority: false,
  hideFromClients: false,
};

export default TaskPanel;
